import { CategoryOutlined, MoreVertOutlined, NumbersOutlined, PinOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TextFieldWithCharacterCount from "components/Form/TextFieldWithCharacterCount";
import { CopyAttributesDialog } from "components/ProductCatalogs/CopyAttributesDialog";
import { AutoPagePaperLayout } from "pages/Layouts/AutoPagePaperLayout";
import { MetaList, MetaListItem } from "pages/Layouts/components/MetaList";
import { PageActions, PageBreadcrumbs, PageSubtitle, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { useState } from "react";
import { Form, Link, useLoaderData } from "react-router-dom";
import { ProductCatalog } from "types/productCatalog";
import WysiwygEditor from "./wysiwygEditor";
import { EbayCategoryFields } from "components/ProductCatalogs/EbayCategoryFields";

const ActionsDropdown = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const actionsOpen = Boolean(anchorEl);
  const [openCopyAttributes, setOpenCopyAttributes] = useState(false);
  return (
    <>
      <CopyAttributesDialog open={openCopyAttributes} onClose={() => setOpenCopyAttributes(false)} />
      <Menu
        open={actionsOpen}
        onClose={() => {
          setAnchorEl(null);
        }}
        onClick={() => {
          setAnchorEl(null);
        }}
        anchorEl={anchorEl}
      >
        <MenuItem onClick={() => setOpenCopyAttributes(true)}>Copy attributes from...</MenuItem>
      </Menu>
      <IconButton
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreVertOutlined />
      </IconButton>
    </>
  );
};

export const EditPage = () => {
  const { product } = useLoaderData() as { product: ProductCatalog };

  const isEditable = product.deviceType.toLowerCase() != "laptop";

  return (
    <AutoPagePaperLayout paperProps={{ sx: { padding: 3 } }}>
      <PageBreadcrumbs links={[{ label: "Resale" }, { label: "Product Catalog", path: "/product-catalog" }]} />
      <PageTitle>Edit Product {product.merchantProductNumber} </PageTitle>
      <PageSubtitle>
        <MetaList>
          <MetaListItem icon={<NumbersOutlined />} label={product.id} title="ID" />
          <MetaListItem icon={<CategoryOutlined />} label={product.deviceType} title="Device Type" />
          <MetaListItem icon={<PinOutlined />} label={product.sku || "undefined"} title="SKU" />
        </MetaList>
      </PageSubtitle>
      <PageActions>{isEditable && <ActionsDropdown />}</PageActions>
      {!isEditable && (
        <Alert severity="info" sx={{ mb: 4 }}>
          Laptops cannot currently be edited in the product catalog. Please contact support if you need to make changes.
        </Alert>
      )}
      <Form key={product.id + product.updatedAt} method="PUT" inert={isEditable ? undefined : true}>
        <Stack gap={2}>
          <TextFieldWithCharacterCount
            label="Title"
            name="product_catalog[title]"
            defaultValue={product.title}
            required={true}
            charLimit={80}
          />
          <TextField
            label="Listing Condition"
            name="product_catalog[condition]"
            defaultValue={product.condition}
            required={true}
            select
          >
            <MenuItem value="A">A</MenuItem>
            <MenuItem value="B">B</MenuItem>
            <MenuItem value="C">C</MenuItem>
            <MenuItem value="D">D</MenuItem>
            <MenuItem value="Used">Used</MenuItem>
          </TextField>
          <TextField
            label="Condition Note"
            name="product_catalog[condition_note]"
            defaultValue={product.conditionNote}
            required={true}
            multiline
          />
          <WysiwygEditor
            label="Description"
            name="product_catalog[description]"
            defaultValue={product.description}
            required={true}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 3 }}>
            <TextField label="Width" name="product_catalog[item_width]" defaultValue={product.itemWidth} />
            <TextField label="Height" name="product_catalog[item_height]" defaultValue={product.itemHeight} />
            <TextField label="Length" name="product_catalog[item_length]" defaultValue={product.itemLength} />
            <TextField label="Weight" name="product_catalog[item_weight]" defaultValue={product.itemWeight} />
          </Box>
          <TextField
            label="Ebay Price"
            name="product_catalog[ebay_price]"
            defaultValue={product.ebayPrice}
            slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
            required={true}
          />
          <EbayCategoryFields product={product} />
          <Box>
            <Typography variant="overline">Resale Photos</Typography>
            <Typography component="p" variant="caption" color="text.secondary">
              Photos used for resale listings. These are managed through the{" "}
              <Link to={product.photosUrl} style={{ textDecoration: "underline" }}>
                inventory edit page
              </Link>
              .
            </Typography>
            {product.hasResalePhotos && (
              <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                {product.resalePhotos?.map((photo) => (
                  <Box
                    key={photo.id}
                    component="img"
                    src={photo.url}
                    alt={photo.filename || "Resale photo"}
                    sx={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "grey.300",
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Box>
            <Typography variant="overline">Stock Photos</Typography>
            <Typography component="p" variant="caption" color="text.secondary">
              Stock photos for this product. These are managed through the{" "}
              <Link to={product.photosUrl} style={{ textDecoration: "underline" }}>
                stock photos page
              </Link>
              .
            </Typography>
            {product.hasStockPhotos && (
              <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                {product.stockPhotos?.map((photo) => (
                  <Box
                    key={photo.id}
                    component="img"
                    src={photo.url}
                    alt={photo.filename || "Stock photo"}
                    sx={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "grey.300",
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Button variant="contained" type="submit" disabled={!isEditable}>
            Save
          </Button>
        </Stack>
      </Form>
    </AutoPagePaperLayout>
  );
};
